import React, { useState } from "react";
import {
  Dialog,
  Typography,
  Grid,
  Box,
  IconButton,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { CheckCircleOutline, Close } from "@mui/icons-material";
import Button from "../../common/Button";
import CustomDatePicker from "../../common/CustomDatePicker";
import InfoPopup from "../../common/InfoPopup";
import { useModalStyles } from "../../common/useModalStyles";

export interface UpdateDateRequest {
    paymentId: string;
    level: string;
    invoiceId: number;
    agencyId: number;
    dateType: "received" | "paid";
    date: string;
}

export type DateUpdateForm = {
    selectedDate: Date | null;
};

interface DateUpdatePopupProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: UpdateDateRequest) => void;
    requestData: UpdateDateRequest | null;
    title?: string;
}

const DateUpdatePopup: React.FC<DateUpdatePopupProps> = ({
  open,
  onClose,
  onConfirm,
  requestData,
  title = "Update Date"
}) => {
  const { control, handleSubmit, reset, trigger } = useForm<DateUpdateForm>({
    defaultValues: { selectedDate: null }
  });
  const [showInfoPopup, setShowInfoPopup] = useState<{ message: string, title: string, show: boolean }>({ message: "", title: "", show: false });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens,
    paperSx,
    headerSx,
    closeIconSx,
    bodySx,
    footerSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  React.useEffect(() => {
    if (open && requestData) {
      reset({
        selectedDate: requestData.date ? dayjs(requestData.date).toDate() : new Date()
      });
    }
  }, [open, requestData, reset]);

  const onSubmit = (formData: DateUpdateForm) => {
    if (!requestData) return;

    const updatedRequest: UpdateDateRequest = {
      ...requestData,
      date: formData.selectedDate
        ? dayjs(formData.selectedDate).format("MM/DD/YYYY")
        : requestData.date
    };

    onConfirm(updatedRequest);
    setShowInfoPopup({ message: "", title: "", show: false });
    onClose();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const handleSave = async () => {

    const isValid = await trigger("selectedDate");
    if (!isValid) {
      return;
    }

    const dateTypeText = requestData?.dateType
      ? requestData.dateType.charAt(0).toUpperCase() + requestData.dateType.slice(1)
      : "Date";

    const currentDate = control._formValues.selectedDate
      ? dayjs(control._formValues.selectedDate).format("MM/DD/YYYY")
      : "No date selected";

    setShowInfoPopup({
      message: `Are you sure you want to update the ${dateTypeText.toLowerCase()} date to ${currentDate}?`,
      title: `Confirm ${dateTypeText} Date Update`,
      show: true
    });
  };

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={handleCancel}
      aria-describedby="date-update-dialog"
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={headerSx}>
          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
              {title}
            </Typography>
            <Typography id="date-update-dialog" sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
              Update the selected commission date.
            </Typography>
          </Box>
          <IconButton onClick={handleCancel} size="small" sx={closeIconSx} aria-label="Close">
            <Close sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>
        <Box sx={bodySx}>
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <CustomDatePicker
                  control={control}
                  controllerName="selectedDate"
                  label={`${requestData?.dateType ? requestData.dateType.charAt(0).toUpperCase() + requestData.dateType.slice(1) : "Update"} Date`}
                  rules={{
                    required: "Date is required."
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={handleSave}
              sx={{
                ...primaryBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "none",
                cursor: "pointer",
                color: "#fff",
                order: { xs: 1, sm: 2 },
              }}
            >
              <CheckCircleOutline sx={{ fontSize: "1.1rem" }} />
              Save
            </Box>
            <Box
              component="button"
              type="button"
              onClick={handleCancel}
              sx={{
                ...cancelBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "1px solid",
                cursor: "pointer",
                order: { xs: 2, sm: 1 },
              }}
            >
              Cancel
            </Box>
          </Box>
        </Box>
      </form>
      {showInfoPopup.show && (
        <InfoPopup
          open={showInfoPopup.show}
          onClose={() => {
            setShowInfoPopup({ message: "", title: "", show: false });
          }}
          content={showInfoPopup.message}
          severity="warning"
          title={showInfoPopup.title}
          confirmButton={"Confirm"}
          onConfirm={handleSubmit(onSubmit)}
        />
      )}
    </Dialog>
  );
};

export default DateUpdatePopup;
